import { useState, useCallback, useEffect } from 'react';
import { useToast } from './use-toast';

interface AdminCredentials {
  username: string;
  password: string;
}

interface AdminUser {
  id: string;
  username: string;
  fullName: string;
  role: string;
}

export function useAdminAuth() {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Login con Basic Auth
  const login = useCallback(async (credentials: AdminCredentials) => {
    setLoading(true);
    setError(null);
    
    try {
      // Crear header Basic Auth
      const authHeader = 'Basic ' + btoa(`${credentials.username}:${credentials.password}`);
      
      const response = await fetch('/api/admin/dashboard/stats', {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
        },
      });
      
      if (!response.ok) {
        throw new Error('Credenciales inválidas');
      }
      
      // Si la petición funciona, guardamos las credenciales
      const adminUser: AdminUser = {
        id: 'admin',
        username: credentials.username,
        fullName: 'Administrador',
        role: 'super_admin',
      };
      
      // Guardar en localStorage para persistencia
      localStorage.setItem('admin_auth', btoa(authHeader));
      localStorage.setItem('admin_user', JSON.stringify(adminUser));
      
      setAdmin(adminUser);
      
      toast({
        title: '✅ Inicio de sesión exitoso',
        description: 'Bienvenido al panel de administración',
      });
      
      return { success: true, admin: adminUser };
      
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error de autenticación';
      setError(message);
      
      toast({
        title: '❌ Error de autenticación',
        description: message,
        variant: 'destructive',
      });
      
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [toast]);

    // Logout
    const logout = useCallback(() => {
      localStorage.removeItem('admin_auth');
      localStorage.removeItem('admin_user');
      setAdmin(null);
      
      toast({
        title: '👋 Sesión cerrada',
        description: 'Has salido del panel de administración',
      });
      
      // Redirigir a login después de cerrar sesión
      setTimeout(() => {
        window.location.href = '/admin/login';
      }, 800); // Reducido a 0.8 segundos
    }, [toast]);

  // Verificar si hay sesión activa al cargar
  const checkAuth = useCallback(() => {
    const authHeader = localStorage.getItem('admin_auth');
    const adminUser = localStorage.getItem('admin_user');
    
    if (authHeader && adminUser) {
      try {
        setAdmin(JSON.parse(adminUser));
        return true;
      } catch {
        logout();
      }
    }
    return false;
  }, [logout]);

  // Obtener headers para peticiones API
  const getAuthHeaders = useCallback(() => {
    const authHeader = localStorage.getItem('admin_auth');
    if (!authHeader) return {};
    
    return {
      'Authorization': atob(authHeader),
    };
  }, []);

  // Verificar autenticación en cada petición
  const withAuth = useCallback(async <T,>(
    fetchFn: (headers: Record<string, string>) => Promise<T>
  ): Promise<T> => {
    const headers = getAuthHeaders();
    
    if (!headers.Authorization) {
      throw new Error('No autenticado');
    }
    
    try {
      return await fetchFn(headers);
    } catch (error) {
      if (error instanceof Error && error.message.includes('401')) {
        logout();
      }
      throw error;
    }
  }, [getAuthHeaders, logout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return {
    admin,
    loading,
    error,
    login,
    logout,
    checkAuth,
    getAuthHeaders,
    withAuth,
    isAuthenticated: !!admin,
  };
}